import { MenuItem, Stack, Typography } from '@mui/material';
import { PublicApiKeyItem } from '@provision/entity-types';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { useMemo } from 'react';
import { CopyToClipboardButton } from 'src/components';
import { DATE_FORMAT } from 'src/pages/Settings/users/const';
import {
  ApiKeysTableWrapper,
  MaskedKeyCell,
  NameCell,
  RevokedChip,
  RevokedText,
} from './styled';

interface ApiKeysTableProps {
  apiKeys: PublicApiKeyItem[];
  isLoading: boolean;
  onEditScopes: (apiKey: PublicApiKeyItem) => void;
  onRegenerate: (apiKey: PublicApiKeyItem) => void;
  onRevoke: (apiKey: PublicApiKeyItem) => void;
}

export const ApiKeysTable = ({
  apiKeys,
  isLoading,
  onEditScopes,
  onRegenerate,
  onRevoke,
}: ApiKeysTableProps) => {
  const columns = useMemo<MRT_ColumnDef<PublicApiKeyItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => {
          const name = row.original.name;
          return (
            <NameCell>
              {row.original.disabled ? <RevokedText>{name}</RevokedText> : name}
              {row.original.disabled ? (
                <RevokedChip
                  label={<Typography variant="overline">REVOKED</Typography>}
                  size="small"
                />
              ) : null}
            </NameCell>
          );
        },
      },
      {
        id: 'key',
        header: 'API Key',
        Cell: ({ row }) => {
          const masked = row.original.maskedApiKey;
          if (row.original.disabled) {
            return (
              <MaskedKeyCell>
                <RevokedText>{masked}</RevokedText>
              </MaskedKeyCell>
            );
          }
          return (
            <MaskedKeyCell>
              <span>{masked}</span>
              <CopyToClipboardButton
                content={row.original.apiKey}
                tooltipText="Copy API key"
                successMessage="API key copied"
              />
            </MaskedKeyCell>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        Cell: ({ row }) => dayjs(row.original.createdAt).format(DATE_FORMAT),
      },
      {
        accessorKey: 'createdBy',
        header: 'Created by',
        Cell: ({ row }) => row.original.createdBy,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: apiKeys,
    defaultColumn: { enableSorting: false, enableColumnActions: false },
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableRowActions: true,
    enableStickyHeader: true,
    muiTablePaperProps: {
      elevation: 0,
      sx: { height: '100%' },
    },
    state: { isLoading },
    initialState: { columnPinning: { right: ['mrt-row-actions'] } },
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      if (row.original.disabled) {
        return [
          <MenuItem key="none" disabled>
            No actions available
          </MenuItem>,
        ];
      }
      return [
        <MenuItem
          key="edit"
          data-testid="api-keys-edit-scopes"
          onClick={() => {
            onEditScopes(row.original);
            closeMenu();
          }}
        >
          Edit scopes
        </MenuItem>,
        <MenuItem
          key="regenerate"
          onClick={() => {
            onRegenerate(row.original);
            closeMenu();
          }}
        >
          Regenerate key
        </MenuItem>,
        <MenuItem
          key="revoke"
          onClick={() => {
            onRevoke(row.original);
            closeMenu();
          }}
        >
          Revoke key
        </MenuItem>,
      ];
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 56,
      },
    },
  });

  if (!apiKeys.length && !isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <Typography variant="body2" color="text.secondary">
          No API keys match the current filters.
        </Typography>
      </Stack>
    );
  }

  return (
    <ApiKeysTableWrapper>
      <MaterialReactTable table={table} />
    </ApiKeysTableWrapper>
  );
};
